import { INPUT_TEXT_TYPES } from 'ui/inputs';
import noop from 'utils/noop';
import {
  DAY_CONFIG,
  HOUR_CONFIG,
  MINUTE_CONFIG,
  Duration,
  defaultErrorState,
} from './DurationForm.constants';
import { DelayInputText, DelayTimeWrapper } from './DurationForm.styles';
import { DurationTimeConfig } from './DurationForm.types';
import { getPlural, isValidNumber } from './DurationForm.utils';

export const DURATION_INPUT_FIELD_ID = 'duration-time-wrapper';
export const DURATION_DAYS_ID = 'duration-days';
export const DURATION_HOURS_ID = 'duration-hours';
export const DURATION_MINUTES_ID = 'duration-minutes';

interface DurationFormProps {
  isReadOnly?: boolean;
  maxDays?: number;
  onUpdateField?: (valueName: Duration, value: number) => void;
  errorState?: Record<Duration, boolean>;
  values: Record<Duration, number | undefined>;
}

/** Shared Duration form to be used in Hold Editor and Delay Editor */
export const DurationForm = ({
  isReadOnly = false,
  maxDays = 365,
  onUpdateField = noop,
  values,
  errorState = defaultErrorState,
  ...rest
}: DurationFormProps) => {
  const {
    [Duration.Day]: daysValue,
    [Duration.Hour]: hoursValue,
    [Duration.Minute]: minutesValue,
  } = values;

  const handleTimeUpdate = (e: string, config: DurationTimeConfig, valueName: Duration) => {
    if (isValidNumber(e, config)) {
      // Get rid of the leading 0
      // Floor it to get rid of the decimal point
      onUpdateField(valueName, Math.floor(Number(e)));
    }
  };

  return (
    <DelayTimeWrapper id={DURATION_INPUT_FIELD_ID} data-test={DURATION_INPUT_FIELD_ID} {...rest}>
      <DelayInputText
        id={DURATION_DAYS_ID}
        hasError={errorState[Duration.Day]}
        data-test={DURATION_DAYS_ID}
        placeholder="0"
        type={INPUT_TEXT_TYPES.NUMBER}
        value={`${daysValue ?? ''}`}
        Suffix={<span>{getPlural(daysValue, Duration.Day)}</span>}
        isReadOnly={isReadOnly}
        onChange={(e) => handleTimeUpdate(e, { ...DAY_CONFIG, max: maxDays }, Duration.Day)}
        {...DAY_CONFIG}
        maxValue={maxDays}
      />
      <DelayInputText
        id={DURATION_HOURS_ID}
        hasError={errorState[Duration.Hour]}
        data-test={DURATION_HOURS_ID}
        placeholder="0"
        type={INPUT_TEXT_TYPES.NUMBER}
        value={`${hoursValue ?? ''}`}
        Suffix={<span>{getPlural(hoursValue, Duration.Hour)}</span>}
        isReadOnly={isReadOnly}
        onChange={(e) => handleTimeUpdate(e, HOUR_CONFIG, Duration.Hour)}
        {...HOUR_CONFIG}
      />
      <DelayInputText
        id={DURATION_MINUTES_ID}
        hasError={errorState[Duration.Minute]}
        data-test={DURATION_MINUTES_ID}
        placeholder="0"
        type={INPUT_TEXT_TYPES.NUMBER}
        value={`${minutesValue ?? ''}`}
        Suffix={<span>{getPlural(minutesValue, Duration.Minute)}</span>}
        isReadOnly={isReadOnly}
        onChange={(e) => handleTimeUpdate(e, MINUTE_CONFIG, Duration.Minute)}
        {...MINUTE_CONFIG}
      />
    </DelayTimeWrapper>
  );
};
